import { getAuthToken, getBaseApiUrl } from './helpers';
import { BulkValidationResponse, IndexMapping, RecipientLeadSheetRecord } from '../types/organizer';

export const doOrganizerLookup = async (accountId: string, lookupValue: string | number) => {
  const url = `${getBaseApiUrl()}/v1/api/archive/${accountId}/organizer/${lookupValue}`;
  const response = await fetch(url, {
    headers: {
      Authorization: await getAuthToken(),
    },
  });
  if (response.ok) {
    const parsedResponse = (await response.json()) as Promise<Record<string, string>>;
    if (!parsedResponse || Object.keys(parsedResponse).length === 0) {
      return Promise.reject('No lookup results found');
    }
    return parsedResponse;
  } else if (response.status === 404) {
    return Promise.reject('Error 404');
  } else {
    return Promise.reject('Call Failed: ' + response.status);
  }
};

export const uploadOrganizerLookupFile = async (accountId: string, file: File | Blob, onUploadProgress?: (progress: number) => void) => {
  const beginResponse = await fetch(`${getBaseApiUrl()}/v1/api/archive/${accountId}/organizer/upload`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: await getAuthToken(),
    },
  });

  const presignedPostData = await beginResponse.json();

  const form = new FormData();
  Object.keys(presignedPostData.fields).forEach((key) => {
    form.append(key, presignedPostData.fields[key]);
  });

  form.append('file', file);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.onreadystatechange = () => {
      if (xhr.readyState == XMLHttpRequest.DONE) {
        if (xhr.status === 200 || xhr.status === 204) {
          resolve(xhr.status);
        } else {
          reject(xhr.status);
        }
      }
    };
    xhr.upload.onerror = () => {
      console.error('Upload failed.', xhr.responseText);
      reject(xhr.status);
    };

    xhr.upload.onprogress = (event) => {
      console.log(`Uploaded ${event.loaded} of ${event.total} bytes`);
      if (onUploadProgress) {
        onUploadProgress(100 * (event.loaded / event.total));
      }
    };

    xhr.open('POST', presignedPostData.url);

    xhr.send(form);
  });
};

function base64ToBlob(base64: string, type = 'application/octet-stream') {
  const binStr = atob(base64);
  const len = binStr.length;
  const arr = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    arr[i] = binStr.charCodeAt(i);
  }
  return new Blob([arr], { type: type });
}

export const printLeadSheets = async (accountId: string, types: string[], indexes: Record<string, string | number | Date>) => {
  const url = `${getBaseApiUrl()}/v1/api/archive/${accountId}/leadsheets/print`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: await getAuthToken(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      accountId,
      types,
      indexes,
    }),
  });
  if (response.ok) {
    const base64Response = await response.text();
    const pdfBlob = base64ToBlob(base64Response, 'application/pdf');
    const link = document.createElement('a');
    link.href = URL.createObjectURL(pdfBlob);
    link.download = 'leadsheets.pdf';
    link.click();
  } else if (response.status === 404) {
    return Promise.reject('Error 404');
  } else {
    return Promise.reject('Call Failed: ' + response.status);
  }
};

export const validateBulkImport = async (
  accountId: string,
  records: RecipientLeadSheetRecord[],
  indexMapping: IndexMapping,
): Promise<BulkValidationResponse> => {
  const url = `${getBaseApiUrl()}/v1/api/archive/${accountId}/leadsheets/bulkimport/validate`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: await getAuthToken(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ records, indexMapping }),
  });

  if (response.ok) {
    return response.json() as Promise<BulkValidationResponse>;
  } else if (response.status === 404) {
    return Promise.reject('Error 404');
  } else {
    return Promise.reject('Call Failed: ' + response.status);
  }
};

export const generateBulkLeadSheets = async (accountId: string, records: RecipientLeadSheetRecord[], indexMapping: IndexMapping): Promise<string> => {
  const url = `${getBaseApiUrl()}/v1/api/archive/${accountId}/leadsheets/bulkprint`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: await getAuthToken(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ records, indexMapping }),
  });

  if (response.ok) {
    return response.text(); // base64 PDF
  } else if (response.status === 404) {
    return Promise.reject('Error 404');
  } else {
    return Promise.reject('Call Failed: ' + response.status);
  }
};

/**
 * Get presigned URL for bulk import CSV upload
 */
export const getBulkImportUploadUrl = async (accountId: string): Promise<{ uploadUrl: string; fields: Record<string, string>; s3Key: string }> => {
  const url = `${getBaseApiUrl()}/v1/api/archive/${accountId}/leadsheets/bulkimport/upload`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: await getAuthToken(),
      'Content-Type': 'application/json',
    },
  });

  if (response.ok) {
    return response.json();
  } else {
    return Promise.reject('Call Failed: ' + response.status);
  }
};

/**
 * Upload CSV file to S3 using presigned URL
 */
export const uploadBulkImportCSV = async (
  file: File,
  uploadUrl: string,
  fields: Record<string, string>,
  onUploadProgress?: (progress: number) => void,
): Promise<void> => {
  const form = new FormData();
  Object.keys(fields).forEach((key) => {
    form.append(key, fields[key]);
  });
  form.append('file', file);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    if (onUploadProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          onUploadProgress(percentComplete);
        }
      });
    }

    xhr.onreadystatechange = () => {
      if (xhr.readyState === XMLHttpRequest.DONE) {
        if (xhr.status === 200 || xhr.status === 204) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      }
    };

    xhr.open('POST', uploadUrl);
    xhr.send(form);
  });
};

/**
 * Process bulk import asynchronously (validates and generates PDF)
 */
export const processBulkImport = async (accountId: string, s3Key: string, indexMapping: IndexMapping): Promise<{ success: boolean; message: string }> => {
  const url = `${getBaseApiUrl()}/v1/api/archive/${accountId}/leadsheets/bulkimport/process`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: await getAuthToken(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ s3Key, indexMapping }),
  });

  if (response.ok) {
    return response.json();
  } else {
    return Promise.reject('Call Failed: ' + response.status);
  }
};
